import conf from '../config';
import { createJob } from './jobs';

const initialState = {
  api: null,
  activeSceneId: null,
  selectedItems: [],
  streaming: false,
  streamingError: false,
  streamingMode: 'move',
  fetchedScenes: {},
};

const SET_API = 'SET_API';
const SET_STREAMING_API = 'SET_STREAMING_API';
const UPDATE_STREAMING_STATUS = 'UPDATE_STREAMING_STATUS';
const SET_STREAM_ERROR = 'SET_STREAM_ERROR';
const ADD_FETCHED_SCENES = 'ADD_FETCHED_SCENES';
const UPDATE_MANIPULATE = 'UPDATE_MANIPULATE';
const SET_SELECTED_MODEL = 'SET_SELECTED_MODEL';

export default (state = initialState, action) => {
  switch (action.type) {
    case SET_API:
      return { ...state, api: action.payload };
    case SET_STREAMING_API:
      return { ...state, streamingApi: action.payload };
    case UPDATE_STREAMING_STATUS:
      return { ...state, streaming: action.payload };
    case ADD_FETCHED_SCENES:
      const fetchedScenes = state.fetchedScenes;
      fetchedScenes[action.payload] = true;
      return { ...state, fetchedScenes };
    case SET_STREAM_ERROR:
      return { ...state, streaming: false, streamingError: action.payload };
    case UPDATE_MANIPULATE:
      return { ...state, streamingMode: action.payload };
    case SET_SELECTED_MODEL:
      return { ...state, selectedModel: action.payload };
    default:
      return state;
  }
};

const setApi = api => ({ type: SET_API, payload: api });

const setStreamingApi = api => ({
  type: SET_STREAMING_API,
  payload: api,
});

export const setStreamError = error => ({
  type: SET_STREAM_ERROR,
  payload: error,
});

const getApi = state => state.player.api;

const getStreamingApi = state => state.player.streamingApi;

const getFetchedScenes = state => state.player.fetchedScenes;

const addFetchedScenes = id => ({ type: ADD_FETCHED_SCENES, payload: id });

export const getStreamingMode = state => state.player.streamingMode;

export const isStreaming = state => state.player.streaming;
export const setStreamingMode = value => ({
  type: UPDATE_MANIPULATE,
  payload: value,
});
export const getStreamingError = state => state.player.streamingError;

export function initializePlayer(sceneId, playerEl, options = {}) {
  return async (dispatch, getState) => {
    const state = getState();
    const hasApi = getApi(state);
    if (!hasApi) {
      const api = window.claraplayer(playerEl);
      await api.sceneIO.fetchAndUse(sceneId, null, { waitForPublish: true });
      dispatch(setApi(api));
    } else {
      await hasApi.sceneIO.clearScene();
      await hasApi.sceneIO.fetchAndUse(sceneId, null, { waitForPublish: true });
    }
  };
}

export function takeSnapshot() {
  return (dispatch, getState) => {
    const state = getState();
    const hasApi = getApi(state);
    if (!hasApi) return;
    hasApi.commands.runCommand('snapshot');
  };
}

export function enterVray(scene, published) {
  return async (dispatch, getState) => {
    const name = scene.friendlyName;
    const asset = scene.assets.slice(-1).pop();
    const sceneId =
      (asset && asset.sceneId) || '8449e924-7311-4b75-9fef-3619a48bd748';
    const state = getState();
    const hasApi = getStreamingApi(state);
    const width = document.getElementById('streamingParent').clientWidth;
    if (!hasApi) {
      const api = window.claraplayer('streamingEl');
      await api.sceneIO.fetchAndUse(sceneId, published);
      if (published) {
        const camera = await api.scene.find(name.replace(/\s/g, '_'));
        api.player.useCamera(camera);
      }
      await api.scene.set(
        {
          name: name.replace(/\s/g, '_') + '_VRscene',
          plug: 'Vrscene',
          property: 'enabled',
        },
        true
      );
      api.player.enterVrayMode(
        'vrayImg',
        width,
        width,
        `wss://blue-office.clara.io/vray/streaming/${sceneId}?width=${width /
          2 -
          10}&height=${width / 2 - 10}&published=${published}`
      );
      let update = false;
      let selectedNode = null;
      ['orbit', 'pan', 'zoom', 'home', 'fullscreen'].forEach(tool =>
        api.player.hideTool(tool)
      );
      const hl = api.selection.setHighlighting(true);
      api.player.addTool({
        name: 'vrayUpdate',
        mousedown: e => {
          const nodes = api.player.filterNodesFromPosition(e);
          const currentState = getState();
          const mode = getStreamingMode(currentState);
          if (nodes.length) {
            const parentNode = api.scene.find({ id: nodes[0], parent: true });
            if (parentNode) {
              if (parentNode !== selectedNode) {
                hl.selectionSet.clear();
                selectedNode = parentNode;
                dispatch({ type: SET_SELECTED_MODEL, payload: selectedNode });
              }
              const childNodes = api.scene.filter({ from: parentNode });
              api.selection.selectNode(parentNode);
              for (let i in childNodes) {
                hl.selectionSet.add(childNodes[i]);
              }
              if (mode === 'move') {
                const opts = {
                  displayGizmo: false,
                  mode: 'plane',
                  plane: { normal: { x: 0, y: 1, z: 0 }, constant: 0 },
                };
                api.commands.setCommandOptions('nodeMove', opts);
                api.commands.activateCommand('nodeMove');
              } else if (mode === 'rotate') {
                api.commands.runCommand('nodeRotate');
                api.commands.updateCommand('nodeRotate', {
                  options: {
                    displayGizmo: false,
                    restrictAxis: 'y',
                  },
                });
              } else {
                console.log('Unknown mode of manipulation');
              }
            }
          } else {
            //leave nodeMove.....
            hl.selectionSet.clear();
            api.commands.activateCommand('orbit');
          }
          update = true;
        },
        mouseup: e => {
          if (update) {
            api.player.updateVrayTranslator();
            update = false;
          }
        },
      });
      window.api = api;
      document.getElementById('vrayImg').style.display = 'block';
      const ws = api._store.getIn(['player', 'ws']);
      ws.onclose = e => {
        const currentState = getState();
        if (currentState.player.streaming) {
          console.log('socket connection interrupted ');
          dispatch(setStreamError(true));
        }
      };
      dispatch(setStreamingApi(api));
      dispatch({ type: UPDATE_STREAMING_STATUS, payload: true });
    } else {
      await hasApi.sceneIO.clearScene();
      await hasApi.sceneIO.fetchAndUse(sceneId);
      let selectedNode = null;
      let update = false;
      const hl = hasApi.selection.setHighlighting(true);
      hasApi.player.addTool({
        name: 'vrayUpdate',
        mousedown: e => {
          const nodes = hasApi.player.filterNodesFromPosition(e);
          const currentState = getState();
          const mode = getStreamingMode(currentState);
          if (nodes.length) {
            const parentNode = hasApi.scene.find({
              id: nodes[0],
              parent: true,
            });
            if (parentNode) {
              if (parentNode !== selectedNode) {
                hl.selectionSet.clear();
                selectedNode = parentNode;
              }
              const childNodes = hasApi.scene.filter({ from: parentNode });
              hasApi.selection.selectNode(parentNode);
              for (let i in childNodes) {
                hl.selectionSet.add(childNodes[i]);
              }
              if (mode === 'move') {
                const opts = {
                  displayGizmo: false,
                  mode: 'plane',
                  plane: { normal: { x: 0, y: 1, z: 0 }, constant: 0 },
                };
                hasApi.commands.setCommandOptions('nodeMove', opts);
                hasApi.commands.activateCommand('nodeMove');
              } else if (mode === 'rotate') {
                hasApi.commands.runCommand('nodeRotate');
                hasApi.commands.updateCommand('nodeRotate', {
                  options: {
                    displayGizmo: false,
                    restrictAxis: 'y',
                  },
                });
              } else {
                console.log('Unknown mode of manipulation');
              }
            }
          } else {
            //leave nodeMove.....
            hl.selectionSet.clear();
            hasApi.commands.activateCommand('orbit');
          }
          update = true;
        },
        mouseup: e => {
          if (update) {
            hasApi.player.updateVrayTranslator();
            update = false;
          }
        },
      });

      hasApi.player.enterVrayMode(
        'vrayImg',
        width,
        500,
        `wss://blue-office.clara.io/vray/streaming/${sceneId}?width=${width}&height=${500}`
      );
      const ws = hasApi._store.getIn(['player', 'ws']);
      ws.on('close', e => {
        console.log('socket closing..........');
      });
    }
  };
}

export function moveModel(x = 0, z = 0) {
  return async (dispatch, getState) => {
    const state = getState();
    const parentId = state.player.selectedModel;
    if (!parentId) return;
    const api = getStreamingApi(state);
    const currentTransform = api.scene.get({
      id: parentId,
      plug: 'Transform',
      property: 'translation',
    });
    const newTransform = Object.assign({}, currentTransform);
    newTransform.x += x;
    newTransform.z += z;
    api.scene.set(
      { id: parentId, plug: 'Transform', property: 'translation' },
      newTransform
    );
    await api._store.getEvaluatedTranslator();
    api.player.updateVrayTranslator();
  };
}

export function rotateModel(y = 0) {
  return async (dispatch, getState) => {
    const state = getState();
    const parentId = state.player.selectedModel;
    if (!parentId) return;
    const api = getStreamingApi(state);
    if (!api) return;
    const currentTransform = api.scene.get({
      id: parentId,
      plug: 'Transform',
      property: 'rotation',
    });
    const newTransform = Object.assign({}, currentTransform);
    newTransform.y += y;
    api.scene.set(
      { id: parentId, plug: 'Transform', property: 'rotation' },
      newTransform
    );
    await api._store.getEvaluatedTranslator();
    api.player.updateVrayTranslator();
  };
}

export function cameraOperation(camera, type, x = 0, y = 0, z = 0) {
  return async (dispatch, getState) => {
    const state = getState();
    const api = getStreamingApi(state);
    if (!api) return;
    const cameraId = api.player.getCamera();
    if (type === 'zoom') {
    } else {
      const currentTransform = api.scene.get({
        id: cameraId,
        plug: 'Transform',
        property: type,
      });
      const newTransform = Object.assign({}, currentTransform);
      newTransform.x += x;
      newTransform.y += y;
      newTransform.z += z;
      await api.scene.set(
        { id: cameraId, plug: 'Transform', property: type },
        newTransform
      );
      await api._store.getEvaluatedTranslator();
      api.player.updateVrayTranslator();
    }
  };
}

export function exitVray() {
  return (dispatch, getState) => {
    const state = getState();
    const api = window.api;
    if (!api) return;
    api.player.exitVrayMode();
    dispatch({ type: UPDATE_STREAMING_STATUS, payload: false });
  };
}

export function changeModel(oldModelId, newModelId) {
  return async (dispatch, getState) => {
    const state = getState();
    const api = window.api;
    if (!api) return;
    if (oldModelId) {
      const oldNodes = api.scene.filter({
        from: oldModelId,
        includeParent: true,
      });

      for (let i in oldNodes) {
        await api.sceneGraph.deleteNode(oldNodes[i]);
      }
    }

    await api.sceneIO.fetch(newModelId);
    const modelId = await api.scene.cloneModel({ sceneId: newModelId });
    await api._store.getEvaluatedTranslator();
    api.player.updateVrayTranslator();
    return modelId;
  };
}

export function savePublished() {
  return async (dispatch, getState) => {
    const state = getState();
    const api = getStreamingApi(state);
    if (!api) return;
    const transit = api.sceneIO.writeScene();
    const form = new FormData();
    form.append('name', 'testPulish');
    form.append('content', transit);
    form.append('type', 'application/vnd.clara.transit-v46+json');
    form.append('sceneId', '7d82957c-9849-498a-bd36-fc5ec0200b25');
    const publishedHash = await fetch('https://clara.io/api/files', {
      method: 'POST',
      body: form,
    })
      .then(res => {
        if (res.status === 200) return res.json();
        return Promise.reject();
      })
      .then(data => data && data.hash);
    return publishedHash;
  };
}

export function applyConfigs(rules, modeId) {
  return async (dispatch, getState) => {
    const state = getState();
    const api = window.api;
    if (!api) return;
    for (let i in rules) {
      const attribute = rules[i];
      for (let i in attribute.values) {
        const nodes = attribute.values[i].nodes;
        const material =
          attribute.values[i].value && attribute.values[i].value[0];
        if (!material || !material.value) continue;
        const materialScene = `0e7bd50a-c34d-4baa-a50a-e2eb1911bdea`;
        const fetchedScenes = getFetchedScenes(state);
        if (!fetchedScenes[materialScene]) {
          await api.sceneIO.fetch(materialScene);
          await api.scene.cloneModel({ sceneId: materialScene });
          dispatch(addFetchedScenes(materialScene));
        }
        const materialId = api.scene.find({
          name: material.value,
          from: materialScene,
        });
        if (!materialId) continue;
        for (let i in nodes) {
          const nodeId = api.scene.find({ name: nodes[i], from: modeId });
          await api.scene.set(
            { id: nodeId, plug: 'Material', property: 'reference' },
            materialId
          );
        }
      }
    }
    api._store.getEvaluatedTranslator();
    api.player.updateVrayTranslator();
  };
}

export const initSpotPoint = (playerEl, file) => async (dispatch, getState) => {
  const spotScene = 'cc2a36fe-0a8d-4670-bf19-29b460af9b18';
  await dispatch(
    await initializePlayer(spotScene, playerEl, { type: 'spotpoint' })
  );
  const state = getState();
  const api = getApi(state);
  if (!api) {
    return;
  } else {
    ['orbit', 'pan', 'zoom'].forEach(tool => api.player.removeTool(tool));
    api.commands.addCommand({
      name: 'spotpoint',
      enabled: true,
      active: true,
      tool: {
        click: function(ev) {
          if (ev.which !== 1) return false;
          const selectedNode = api.player.filterNodesFromPosition(ev);
          if (selectedNode.length) {
            const rect = ev.rect;
            const x = ev.clientX;
            const y = ev.clientY;
            const ndc = {
              x: (2 * x) / rect.width - 1,
              y: (-2 * y) / rect.height + 1,
            };
            const hits = api._store.getTranslator().raycastSelect(ndc);
            const hit = hits[0];
            if (hit) {
              const uv = hit.data[0].uv;
              const spotX = (uv.x * 100).toFixed(2);
              const spotY = ((1 - uv.y) * 100).toFixed(2);
              const spotXEl = document.getElementById('spotX');
              const spotYEl = document.getElementById('spotY');
              spotXEl.innerText = spotX;
              spotYEl.innerText = spotY;
            }
          }
        },
      },
    });
    if (file) {
      await dispatch(await importFile(file));
    }
  }
};

export const importFile = zipFile => async (dispatch, getState) => {
  const data = {
    type: 'create-texture',
    syncallback: true,
  };
  const result = await dispatch(await createJob(data, zipFile));
  if (result) {
    const state = getState();
    const api = getApi(state);
    const thumbnail = result.data;
    if (!api) {
      console.log('At this point we should have an api...');
      return;
    } else {
      const asset = await api.assets.importImage(thumbnail);
      const mtl = await api.scene.find('Physical');
      await api.scene.set(
        { id: mtl, plug: 'Material', property: 'baseMap' },
        asset.imageNodeId
      );
    }
  }
};



// WEBPACK FOOTER //
// ./src/modules/player.js